import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tfoot,
  Input,
  Button,
  Flex,
  Text,
  Badge,
  Spinner,
  HStack,
  VStack,
  IconButton,
  Tooltip,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Progress,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, AddIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import AdminPilotOutreachTab from './AdminPilotOutreachTab';
import AdminBehaviourChangeTab from './AdminBehaviourChangeTab';
import {
  FileText, MessageSquare, BarChart3, Target, FileDown, Share2, Plug, Layers,
  TrendingUp, ArrowUpCircle, User, Tag as TagIcon, Activity, Clock,
  Calendar, Star, Shield, Zap, DollarSign, Hash, HelpCircle, MoreVertical,
  Lightbulb, Radio
} from 'lucide-react';

const SEGMENTS = ['Solo Founders', 'Consultants & Fractional Operators', 'Growth/Marketing/Product Leads', 'Innovation/Business Analysts', 'Uncategorized'];
const STATUSES = ['Active', 'Paused', 'Completed', 'Dropped'];

const verdictColor = (v) => {
  switch (v) {
    case 'Champion': return 'green';
    case 'Engaged': return 'blue';
    case 'Exploring': return 'yellow';
    case 'At Risk': return 'orange';
    case 'Inactive': return 'red';
    case 'New': return 'gray';
    default: return 'gray';
  }
};

const scoreColor = (score) => {
  if (score >= 60) return 'green';
  if (score >= 40) return 'blue';
  if (score >= 20) return 'yellow';
  return 'red';
};

const IconHeader = ({ icon: Icon, label, size = 14 }) => (
  <Tooltip label={label} fontSize="xs" hasArrow>
    <Flex align="center" justify="center" gap={1} cursor="default">
      <Icon size={size} />
    </Flex>
  </Tooltip>
);

const planTypeColor = (type) => {
  switch (type) {
    case 'pro': return 'green';
    case 'team': return 'purple';
    default: return 'gray';
  }
};

const criterionColor = (current, target) => {
  const ratio = target > 0 ? current / target : 0;
  if (ratio >= 0.9) return 'green';
  if (ratio >= 0.6) return 'yellow';
  return 'red';
};

const AdminPilotTracker = () => {
  const [participants, setParticipants] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [notesMap, setNotesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [enrolling, setEnrolling] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteValue, setEditNoteValue] = useState('');
  const [panelParticipant, setPanelParticipant] = useState(null);
  const [panelFilter, setPanelFilter] = useState('all');
  const [newNoteType, setNewNoteType] = useState('week1_feedback');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteRating, setNewNoteRating] = useState(0);
  const [selectedNote, setSelectedNote] = useState(null);
  const toast = useToast();

  const fetchParticipants = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/admin/pilot/participants');
      if (res.ok) setParticipants(await res.json());
    } catch (e) {
      console.error('Error fetching participants:', e);
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/admin/pilot/success-dashboard');
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        setPlatformStats({
          validationMetrics: data.validationMetrics || {},
          adoptionMetrics: data.adoptionMetrics || {},
          projectMetrics: data.projectMetrics || {},
        });
      }
    } catch (e) {
      console.error('Error fetching dashboard:', e);
    }
  }, []);

  const fetchFeedbackSummary = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/admin/pilot/feedback-summary');
      if (res.ok) setFeedbackSummary(await res.json());
    } catch (e) {
      console.error('Error fetching feedback summary:', e);
    }
  }, []);

  const fetchNotes = useCallback(async (participantId) => {
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/notes/${participantId}`);
      if (res.ok) {
        const data = await res.json();
        setNotesMap(prev => ({ ...prev, [participantId]: data }));
      }
    } catch (e) {
      console.error('Error fetching notes:', e);
    }
  }, []);


  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchParticipants(),
        fetchDashboard(),
        fetchFeedbackSummary(),
      ]);
      setLoading(false);
    };
    load();
  }, [fetchParticipants, fetchDashboard, fetchFeedbackSummary]);

  useEffect(() => {
    // Activity is the third tab; it loads participant notes when opened.
    if (tabIndex === 2 && participants.length > 0) {
      const toFetch = participants.filter(p => !notesMap[p.id]);
      toFetch.forEach(p => fetchNotes(p.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex, participants, fetchNotes]);

  const updateParticipant = async (id, field, value) => {
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/participants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        await fetchParticipants();
        toast({ title: 'Updated', status: 'success', duration: 2000 });
      } else throw new Error('Update failed');
    } catch (e) {
      toast({ title: 'Error', description: 'Could not update', status: 'error', duration: 3000 });
    }
  };

  const deleteParticipant = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/participants/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setParticipants(prev => prev.filter(p => p.id !== id));
        toast({ title: 'Removed from pilot', status: 'success', duration: 2000 });
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const enrollAllUsers = async () => {
    setEnrolling(true);
    try {
      const res = await fetchWithAuth('/api/admin/pilot/enroll-all', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast({ title: `Enrolled ${data.enrolled} users`, status: 'success', duration: 3000 });
        await fetchParticipants();
      }
    } catch (e) {
      toast({ title: 'Error enrolling users', status: 'error', duration: 3000 });
    } finally {
      setEnrolling(false);
    }
  };

  const addNoteFromPanel = async () => {
    if (!newNoteContent.trim() || !panelParticipant) return;
    try {
      const res = await fetchWithAuth('/api/admin/pilot/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_id: panelParticipant.id, note_type: newNoteType, content: newNoteContent.trim(), rating: newNoteRating || null }),
      });
      if (res.ok) {
        toast({ title: 'Note added', status: 'success', duration: 2000 });
        setNewNoteContent('');
        setNewNoteRating(0);
        await Promise.all([fetchNotes(panelParticipant.id), fetchFeedbackSummary()]);
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const updateNoteRating = async (noteId, participantId, rating) => {
    try {
      await fetchWithAuth(`/api/admin/pilot/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating }),
      });
      setSelectedNote(prev => prev && prev.id === noteId ? { ...prev, rating } : prev);
      await fetchNotes(participantId);
    } catch (e) {
      toast({ title: 'Error updating rating', status: 'error', duration: 3000 });
    }
  };

  const openPanel = (participant, filterType) => {
    setPanelParticipant(participant);
    setPanelFilter(filterType || 'all');
    setNewNoteType(filterType && filterType !== 'all' ? filterType : 'week1_feedback');
    setNewNoteContent('');
    setEditingNote(null);
    fetchNotes(participant.id);
  };

  const closePanel = () => {
    setPanelParticipant(null);
    setPanelFilter('all');
    setNewNoteContent('');
    setNewNoteRating(0);
    setEditingNote(null);
    setSelectedNote(null);
  };

  const updateNote = async (noteId, participantId) => {
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/notes/${noteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editNoteValue }),
      });
      if (res.ok) {
        setEditingNote(null);
        setEditNoteValue('');
        await Promise.all([fetchNotes(participantId), fetchFeedbackSummary()]);
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const deleteNote = async (noteId, participantId) => {
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/notes/${noteId}`, { method: 'DELETE' });
      if (res.ok) await Promise.all([fetchNotes(participantId), fetchFeedbackSummary()]);
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const getName = (p) => {
    const first = p.first_name || '';
    const last = p.last_name || '';
    return (first + ' ' + last).trim() || p.email || '—';
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString();
  };

  const formatDaysAgo = (days) => {
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  };

  // Fades from solid black (recent, days) to lighter grays (weeks, then months) as activity ages.
  const activityAgeColor = (days) => {
    if (days == null) return 'rgba(0,0,0,0.5)';
    if (days < 7) return 'rgba(0,0,0,1)';
    if (days < 30) return 'rgba(0,0,0,0.75)';
    return 'rgba(0,0,0,0.5)';
  };

  const formatDaysInPilot = (days) => {
    if (days == null) return '—';
    if (days < 30) return `${days}d`;
    return `${Math.floor(days / 30)}mo`;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="lg" />
      </Box>
    );
  }

  const segmentStats = {};
  participants.forEach(p => {
    const seg = p.segment || 'Uncategorized';
    if (!segmentStats[seg]) segmentStats[seg] = { count: 0, totalScore: 0, champions: 0, atRisk: 0 };
    segmentStats[seg].count++;
    segmentStats[seg].totalScore += p.engagement_score || 0;
    if (p.auto_verdict === 'Champion') segmentStats[seg].champions++;
    if (p.auto_verdict === 'At Risk' || p.auto_verdict === 'Inactive') segmentStats[seg].atRisk++;
  });

  const verdictOrder = { 'Champion': 0, 'Engaged': 1, 'Exploring': 2, 'At Risk': 3, 'Inactive': 4, 'New': 5 };
  const sortedByVerdict = [...participants].sort((a, b) => {
    const va = verdictOrder[a.auto_verdict] ?? 5;
    const vb = verdictOrder[b.auto_verdict] ?? 5;
    if (va !== vb) return va - vb;
    return (b.engagement_score || 0) - (a.engagement_score || 0);
  });

  return (
    <Box minW={0}>
      <Flex mb={6} justify="space-between" align="center" wrap="wrap" gap={3}>
        <Heading size="lg">Pilot Tracker</Heading>
        <HStack spacing={3}>
          <Text fontSize="xs" color="gray.500">New users are auto-enrolled</Text>
          <Button size="sm" colorScheme="green" onClick={enrollAllUsers} isLoading={enrolling}>
            Enroll All Users
          </Button>
        </HStack>
      </Flex>

      {participants.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
          <Card variant="outline" size="sm">
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Participants</StatLabel>
                <StatNumber fontSize="2xl">{participants.length}</StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  {participants.filter(p => p.auto_verdict === 'Champion').length} champions
                </StatHelpText>
                <Text fontSize="xs" color="gray.400">Total enrolled pilot users</Text>
              </Stat>
            </CardBody>
          </Card>
          <Card variant="outline" size="sm">
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Avg Engagement</StatLabel>
                <StatNumber fontSize="2xl">
                  {participants.length > 0 ? Math.round(participants.reduce((s, p) => s + (p.engagement_score || 0), 0) / participants.length) : 0}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>scored from activity signals</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card variant="outline" size="sm">
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Needs Attention</StatLabel>
                <StatNumber fontSize="2xl" color="orange.500">
                  {participants.filter(p => p.auto_verdict === 'At Risk' || p.auto_verdict === 'Inactive').length}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>at risk + inactive</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card variant="outline" size="sm">
            <CardBody py={3}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Upgrade Funnel</StatLabel>
                <StatNumber fontSize="2xl">
                  {participants.filter(p => p.has_upgrade_intent).length}
                </StatNumber>
                <StatHelpText fontSize="xs" mb={0}>
                  {participants.filter(p => p.upgrade_converted > 0).length} converted
                </StatHelpText>
                <Text fontSize="xs" color="gray.400">showed interest in upgrading</Text>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      <Tabs minW={0} index={tabIndex} onChange={setTabIndex} colorScheme="blue" variant="enclosed">
        <TabList overflowX="auto" overflowY="hidden" sx={{ "& > button": { flexShrink: 0 } }}>
          <Tab>Behaviour Change</Tab>
          <Tab>Health</Tab>
          <Tab>Activity</Tab>
          <Tab>Feedback</Tab>
          <Tab>Market Fit</Tab>
          <Tab>Outreach</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            {tabIndex === 0 && <AdminBehaviourChangeTab />}
          </TabPanel>

          <TabPanel px={0}>
            {/* Section A — Pilot Metrics */}
            {platformStats && (
              <Box mb={6}>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>Pilot Metrics <Badge variant="outline" fontSize="xs" colorScheme="gray" ml={1}>Active Pilot Users</Badge></Text>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  User metrics count people once and show their cohort in each card. Project metrics count each project, so one highly active user can contribute multiple projects.
                </Text>
                {[
                  {
                    title: 'User Metrics',
                    note: 'Breadth across the pilot cohort. Each active pilot user counts once.',
                    metrics: [
                      {
                        label: 'Best Fit Segment',
                        value: dashboard?.best_segment?.fit_score || 0,
                        sub: dashboard?.best_segment?.segment || 'No segment yet',
                        displayMode: 'segment',
                        tooltip: dashboard?.best_segment
                          ? `Directional PMF fit score for ${dashboard.best_segment.segment}. Weighted by would-pay (${dashboard.best_segment.would_pay_rate}%), essential rating (${dashboard.best_segment.essential_rate}%), recent activity (${dashboard.best_segment.active_rate}%), and cohort size confidence.`
                          : 'Directional PMF fit score based on segment-level would-pay, essential rating, recent activity, and cohort size confidence.',
                        green: 55, orange: 35,
                      },
                      {
                        label: 'Strategy Within 24h',
                        value: platformStats.validationMetrics?.sameDayActivationRate || 0,
                        sub: `${platformStats.validationMetrics?.activatedSameDay || 0}/${platformStats.validationMetrics?.totalUsersForActivation || 0} users created strategy fast`,
                        tooltip: 'Active pilot users who created their first Product Strategy within 24 hours of signup. This measures immediate pull into the core product moment.',
                        green: 40, orange: 20,
                      },
                      {
                        label: 'Created First Strategy',
                        value: platformStats.validationMetrics?.firstStrategyActivationRate || 0,
                        sub: `${platformStats.validationMetrics?.firstStrategyActivatedUsers || 0}/${platformStats.validationMetrics?.totalUsersForFirstStrategyActivation || 0} users created strategy`,
                        tooltip: 'Active pilot users who created at least one Product Strategy at any point. This measures whether they reached the first real product outcome.',
                        green: 70, orange: 45,
                      },
                      {
                        label: 'Returned After Day 7',
                        value: platformStats.validationMetrics?.pilotRetentionRate || 0,
                        sub: `${platformStats.validationMetrics?.returningPilotUsers || 0}/${platformStats.validationMetrics?.totalPilotUsersForRetention || 0} pilot users came back`,
                        tooltip: 'Cohort metric: active pilot users with real user activity more than 7 days after signup. This measures return behavior across the full active pilot cohort, including users who never created a strategy.',
                        green: 40, orange: 20,
                      },
                      {
                        label: 'Strategy Users Returned',
                        value: platformStats.validationMetrics?.returnUserRate || 0,
                        sub: `${platformStats.validationMetrics?.returningUsers || 0}/${platformStats.validationMetrics?.totalUsersWithStrategies || 0} strategy users came back`,
                        tooltip: 'Cohort metric: users who created at least one Product Strategy and later had real user activity more than 7 days after signup. This is the activated-user retention view, not current weekly activity.',
                        green: 40, orange: 20,
                      },
                    ],
                  },
                  {
                    title: 'Project Metrics',
                    note: 'Depth across projects. One active user with many projects can contribute many times.',
                    metrics: [
                      {
                        label: 'Projects With Brief Edits',
                        value: platformStats.validationMetrics?.planEditRate || 0,
                        sub: `${platformStats.validationMetrics?.editedPlans || 0}/${platformStats.validationMetrics?.totalPlansForCompletion || 0} projects edited after creation`,
                        tooltip: 'Project-level metric: projects where the brief content was edited after initial creation. This avoids counting background saves, generated reports, or pulse updates as refinement.',
                        green: 30, orange: 15,
                      },
                      {
                        label: 'Projects With Chat',
                        value: platformStats.validationMetrics?.chatEngagementRate || 0,
                        sub: `${platformStats.validationMetrics?.plansWithChat || 0}/${platformStats.validationMetrics?.totalPlansForCompletion || 0} projects used chat`,
                        tooltip: 'Project-level metric: projects where the AI chat assistant was used. This shows depth of use inside projects, not how many distinct users chatted.',
                        green: 50, orange: 25,
                      },
                      {
                        label: 'Projects With Delegated Work',
                        value: platformStats.projectMetrics?.taskProjectRate || 0,
                        sub: `${platformStats.projectMetrics?.projectsWithTasks || 0}/${platformStats.projectMetrics?.totalProjects || 0} projects with completed user-visible agent work`,
                        tooltip: 'Project-level metric: projects with at least one completed user-visible agent task. Pulse, engagement, reminder, and background system jobs are excluded.',
                        green: 30, orange: 15,
                      },
                      {
                        label: 'Projects With Learnings',
                        value: platformStats.projectMetrics?.learningProjectRate || 0,
                        sub: `${platformStats.projectMetrics?.projectsWithLearnings || 0}/${platformStats.projectMetrics?.totalProjects || 0} projects with learnings`,
                        tooltip: 'Project-level metric: projects with at least one captured learning. Shows whether PABS is building reusable memory from project work.',
                        green: 30, orange: 15,
                      },
                      {
                        label: 'Projects With Pulses',
                        value: platformStats.projectMetrics?.pulseProjectRate || 0,
                        sub: `${platformStats.projectMetrics?.projectsWithPulses || 0}/${platformStats.projectMetrics?.totalProjects || 0} projects with pulses`,
                        tooltip: 'Project-level metric: projects with at least one strategy pulse. Shows whether projects are entering the ongoing strategy review loop.',
                        green: 25, orange: 10,
                      },
                      {
                        label: 'Projects With Action Plans',
                        value: platformStats.validationMetrics?.actionPlanRate || 0,
                        sub: `${platformStats.validationMetrics?.plansWithPrograms || 0}/${platformStats.validationMetrics?.totalPlansForCompletion || 0} projects with action plans`,
                        tooltip: 'Project-level metric: projects where users created action programs with stages and tools. This shows movement from strategy to execution planning.',
                        green: 20, orange: 10,
                      },
                      {
                        label: 'Projects With Docs',
                        value: platformStats.validationMetrics?.docGenerationRate || 0,
                        sub: `${platformStats.validationMetrics?.plansWithDocs || 0}/${platformStats.validationMetrics?.totalPlansForCompletion || 0} projects shipped docs`,
                        tooltip: 'Project-level metric: projects where users exported a document such as a PRD or venture doc. One active user can contribute multiple exported projects.',
                        green: 30, orange: 15,
                      },
                      {
                        label: 'Projects With MCP',
                        value: platformStats.projectMetrics?.shipProjectRate || 0,
                        sub: `${platformStats.projectMetrics?.projectsWithMcp || 0}/${platformStats.projectMetrics?.totalProjects || 0} projects with MCP`,
                        tooltip: 'Project-level metric: projects with a Ship/MCP token for external AI builders such as Cursor, Replit, or Lovable.',
                        green: 20, orange: 10,
                      },
                    ],
                  },
                ].map(group => (
                  <Box key={group.title} mb={4}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>{group.title}</Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>{group.note}</Text>
                    <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={3}>
                      {group.metrics.map(({ label, value, sub, tooltip, green, orange, displayMode }) => {
                        const color = value >= green ? 'green' : value >= orange ? 'orange' : 'red';
                        const isSegmentMetric = displayMode === 'segment';
                        return (
                          <Tooltip key={label} label={tooltip} hasArrow placement="top" maxW="260px">
                            <Box textAlign="center" p={3} bg={`${color}.50`} borderRadius="md" cursor="help" minH="94px">
                              {isSegmentMetric ? (
                                <>
                                  <Text fontSize="xs" color="gray.600" fontWeight="bold" mb={1}>{label}</Text>
                                  <Text
                                    fontSize={{ base: 'sm', md: 'md' }}
                                    color="gray.800"
                                    fontWeight="bold"
                                    lineHeight="1.2"
                                    noOfLines={2}
                                  >
                                    {sub}
                                  </Text>
                                  <Text fontSize="sm" fontWeight="bold" color={`${color}.600`} mt={1}>
                                    {value}% fit score
                                  </Text>
                                </>
                              ) : (
                                <>
                                  <Text fontSize="2xl" fontWeight="bold" color={`${color}.600`}>{value}%</Text>
                                  <Text fontSize="xs" color="gray.600" fontWeight="medium">{label}</Text>
                                  <Text fontSize="xs" color="gray.400">{sub}</Text>
                                </>
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                ))}
              </Box>
            )}

            {/* Section B — Pilot Success Criteria */}
            {dashboard && (
              <Box mb={6}>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>Pilot Success Criteria <Badge variant="outline" fontSize="xs" colorScheme="purple" ml={1}>Pilot Cohort</Badge></Text>
                <Text fontSize="xs" color="gray.500" mb={3}>Green = on track · Yellow = close · Red = below target. Targets based on what a healthy pilot achieves by week 4.</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={4}>
                  {(dashboard.criteria || []).map((c, idx) => {
                    const color = criterionColor(c.current, c.target);
                    const pct = c.target > 0 ? Math.min((c.current / c.target) * 100, 100) : 0;
                    return (
                      <Card key={idx} variant="outline">
                        <CardBody>
                          <Flex justify="space-between" align="center" mb={1}>
                            <Text fontSize="sm" fontWeight="medium">{c.label}</Text>
                            <Badge colorScheme={color} variant="subtle" fontSize="xs">{c.current}% / {c.target}%</Badge>
                          </Flex>
                          {c.description && (
                            <Text fontSize="xs" color="gray.500" mb={2} lineHeight="1.4">{c.description}</Text>
                          )}
                          <Progress value={pct} size="md" colorScheme={color} borderRadius="full" mb={2} />
                          <Flex justify="space-between">
                            <Text fontSize="xs" color="gray.500">{c.raw_count} of {dashboard.total_participants} users</Text>
                            <Text fontSize="xs" color={`${color}.600`} fontWeight="medium">{pct >= 90 ? 'Met' : pct >= 60 ? 'Close' : 'Below'}</Text>
                          </Flex>
                        </CardBody>
                      </Card>
                    );
                  })}
                </SimpleGrid>
              </Box>
            )}

          </TabPanel>

          <TabPanel px={0}>
            <Text fontSize="sm" color="gray.500" mb={3}>
              All per-participant data in one view. Manage segments, track weekly pilot activity, and see raw counts alongside behavioural signals. Hover icons for column names.
            </Text>
            <Box bg="white" borderRadius="md" overflow="auto" boxShadow="sm">
              <Table size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    {/* Identity */}
                    <Th minW="160px"><Flex align="center" gap={1}><User size={13} /> Name</Flex></Th>
                    {/* Management */}
                    <Th minW="130px"><IconHeader icon={TagIcon} label="Segment" /></Th>
                    {/* Engagement */}
                    <Th textAlign="center"><IconHeader icon={Zap} label="Engagement Score" /></Th>
                    <Th textAlign="center"><IconHeader icon={Shield} label="Verdict" /></Th>
                    <Th textAlign="center"><IconHeader icon={Clock} label="Last User Activity" /></Th>
                    <Th textAlign="center"><IconHeader icon={Calendar} label="Days in Pilot" /></Th>
                    <Th textAlign="center"><IconHeader icon={Star} label="Rating" /></Th>
                    {/* Raw counts */}
                    <Th textAlign="center"><IconHeader icon={FileText} label="Briefs (≥2 = green)" /></Th>
                    <Th textAlign="center"><IconHeader icon={MessageSquare} label="Chat Conversations" /></Th>
                    <Th textAlign="center"><IconHeader icon={BarChart3} label="Strategy Generations" /></Th>
                    <Th textAlign="center"><IconHeader icon={Target} label="Competitor Analyses" /></Th>
                    <Th textAlign="center"><IconHeader icon={Activity} label="Action Plans Generated" /></Th>
                    <Th textAlign="center" bg="brand.100"><IconHeader icon={FileDown} label="Doc Exports" /></Th>
                    <Th textAlign="center" bg="brand.100"><IconHeader icon={Share2} label="Shares Created" /></Th>
                    <Th textAlign="center" bg="brand.100"><IconHeader icon={Plug} label="MCP Connected" /></Th>
                    <Th textAlign="center" bg="brand.200"><IconHeader icon={Lightbulb} label="Learnings" /></Th>
                    <Th textAlign="center" bg="brand.200"><IconHeader icon={Zap} label="Agent Tasks" /></Th>
                    <Th textAlign="center" bg="brand.200"><IconHeader icon={Radio} label="Pulses" /></Th>
                    <Th textAlign="center"><IconHeader icon={Layers} label="Total Generations" /></Th>
                    <Th textAlign="center"><IconHeader icon={ArrowUpCircle} label="Plan Type" /></Th>
                    <Th textAlign="center"><IconHeader icon={TrendingUp} label="Upgrade Intent" /></Th>
                    {/* Pilot timeline */}
                    <Th textAlign="center"><IconHeader icon={Calendar} label="Week 1 Used" /></Th>
                    <Th textAlign="center"><IconHeader icon={Activity} label="Week 2-3 Active" /></Th>
                    <Th w="36px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sortedByVerdict.map((p) => (
                    <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                      {/* Identity */}
                      <Td>
                        <HStack spacing={2} align="center">
                          <Tooltip label={p.status || 'Active'} fontSize="xs" hasArrow placement="top">
                            <Box
                              w="8px" h="8px" borderRadius="full" flexShrink={0}
                              bg={
                                p.status === 'Active' ? 'green.400' :
                                p.status === 'Paused' ? 'yellow.400' :
                                p.status === 'Completed' ? 'blue.400' :
                                p.status === 'Dropped' ? 'red.400' : 'gray.400'
                              }
                            />
                          </Tooltip>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">{getName(p)}</Text>
                            <Text fontSize="xs" color="gray.400">{p.email}</Text>
                          </VStack>
                        </HStack>
                      </Td>
                      {/* Management */}
                      <Td>
                        <Select size="xs" value={p.segment || 'Uncategorized'} onChange={(e) => updateParticipant(p.id, 'segment', e.target.value)} w="130px">
                          {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </Select>
                      </Td>
                      {/* Engagement */}
                      <Td>
                        <Tooltip label={`Score: ${p.engagement_score}/100`}>
                          <HStack spacing={1}>
                            <Progress value={p.engagement_score || 0} size="sm" w="50px" colorScheme={scoreColor(p.engagement_score)} borderRadius="full" />
                            <Text fontSize="xs" fontWeight="bold" color={`${scoreColor(p.engagement_score)}.600`}>{p.engagement_score || 0}</Text>
                          </HStack>
                        </Tooltip>
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={verdictColor(p.auto_verdict)} variant="solid" fontSize="xs">
                          {p.auto_verdict || 'New'}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="xs" color={activityAgeColor(p.days_since_active)}>
                          {formatDaysAgo(p.days_since_active)}
                        </Text>
                      </Td>
                      <Td><Text fontSize="xs">{formatDaysInPilot(p.days_in_pilot)}</Text></Td>
                      <Td textAlign="center">
                        <Text fontSize="xs">{p.overall_rating ? `★${p.overall_rating}` : '—'}</Text>
                      </Td>
                      {/* Raw counts — Strategy goes green at ≥2 per plan (iteration success threshold) */}
                      <Td textAlign="center">
                        <Badge
                          colorScheme={parseInt(p.plans_created) >= 1 ? 'blue' : 'gray'}
                          variant={parseInt(p.plans_created) > 0 ? 'solid' : 'outline'}
                        >
                          {p.plans_created || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={parseInt(p.chat_count) > 0 ? 'green' : 'gray'} variant={parseInt(p.chat_count) > 0 ? 'solid' : 'outline'}>
                          {p.chat_count || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Badge
                          colorScheme={p.has_iterated_strategy ? 'green' : parseInt(p.strategy_generations) > 0 ? 'purple' : 'gray'}
                          variant={parseInt(p.strategy_generations) > 0 ? 'solid' : 'outline'}
                          title={p.max_strategies_per_plan ? `Max ${p.max_strategies_per_plan} per plan` : undefined}
                        >
                          {p.strategy_generations || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={parseInt(p.competitor_generations) > 0 ? 'red' : 'gray'} variant={parseInt(p.competitor_generations) > 0 ? 'solid' : 'outline'}>
                          {p.competitor_generations || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={p.has_action_plans ? 'teal' : 'gray'} variant={p.has_action_plans ? 'solid' : 'outline'} fontSize="xs">
                          {p.action_plans_count || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center" bg="brand.100">
                        <Badge colorScheme={parseInt(p.exports_made) > 0 ? 'teal' : 'gray'} variant={parseInt(p.exports_made) > 0 ? 'solid' : 'outline'}>
                          {p.exports_made || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center" bg="brand.100">
                        <Badge colorScheme={parseInt(p.shares_created) > 0 ? 'orange' : 'gray'} variant={parseInt(p.shares_created) > 0 ? 'solid' : 'outline'}>
                          {p.shares_created || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center" bg="brand.100">
                        <Badge colorScheme={p.has_mcp ? 'green' : 'gray'} variant="subtle">
                          {p.has_mcp ? 'Yes' : 'No'}
                        </Badge>
                      </Td>
                      <Td textAlign="center" bg="brand.200">
                        <Badge colorScheme={p.learnings_count > 0 ? 'yellow' : 'gray'} variant={p.learnings_count > 0 ? 'solid' : 'outline'} fontSize="xs">
                          {p.learnings_count || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center" bg="brand.200">
                        <Tooltip label={`${p.agent_tasks_completed || 0} completed`} fontSize="xs" hasArrow>
                          <Badge
                            colorScheme={p.agent_task_count > 5 ? 'purple' : p.agent_task_count > 0 ? 'blue' : 'gray'}
                            variant={p.agent_task_count > 0 ? 'solid' : 'outline'}
                            fontSize="xs"
                            cursor="default"
                          >
                            {p.agent_task_count || 0}
                          </Badge>
                        </Tooltip>
                      </Td>
                      <Td textAlign="center" bg="brand.200">
                        <Badge colorScheme={p.pulses_count > 0 ? 'cyan' : 'gray'} variant={p.pulses_count > 0 ? 'solid' : 'outline'} fontSize="xs">
                          {p.pulses_count || 0}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Text fontSize="sm" fontWeight="bold" color={p.total_generations > 0 ? 'blue.600' : 'gray.400'}>
                          {p.total_generations || 0}
                        </Text>
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={planTypeColor(p.plan_type)} variant="solid" fontSize="xs" textTransform="capitalize">
                          {p.plan_type || 'free'}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Badge colorScheme={p.has_upgrade_intent ? 'orange' : 'gray'} variant={p.has_upgrade_intent ? 'solid' : 'outline'} fontSize="xs">
                          {p.has_upgrade_intent ? `Yes (${p.upgrade_intents})` : 'No'}
                        </Badge>
                      </Td>
                      {/* Pilot timeline */}
                      <Td textAlign="center">
                        <Badge
                          colorScheme={p.week1_active ? 'green' : 'gray'}
                          variant={p.week1_active ? 'solid' : 'outline'}
                          fontSize="xs"
                        >
                          {p.week1_active ? 'Yes' : 'No'}
                        </Badge>
                      </Td>
                      <Td textAlign="center">
                        <Badge
                          colorScheme={p.week2_3_active_auto ? 'green' : 'gray'}
                          variant={p.week2_3_active_auto ? 'solid' : 'outline'}
                          fontSize="xs"
                        >
                          {p.week2_3_active_auto ? 'Yes' : 'No'}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu isLazy>
                          <MenuButton as={IconButton} icon={<MoreVertical size={14} />} size="xs" variant="ghost" aria-label="Actions" />
                          <MenuList fontSize="xs" minW="160px">
                            <MenuItem isDisabled fontWeight="bold" fontSize="10px" color="gray.500" textTransform="uppercase">Status</MenuItem>
                            {STATUSES.map(s => (
                              <MenuItem key={s} onClick={() => updateParticipant(p.id, 'status', s)}
                                fontWeight={p.status === s ? 'bold' : 'normal'}>
                                {s} {p.status === s ? '✓' : ''}
                              </MenuItem>
                            ))}
                            <MenuDivider />
                            <MenuItem isDisabled fontWeight="bold" fontSize="10px" color="gray.500" textTransform="uppercase">Rating</MenuItem>
                            {[1,2,3,4,5].map(n => (
                              <MenuItem key={n} onClick={() => updateParticipant(p.id, 'overall_rating', n)}
                                fontWeight={p.overall_rating === n ? 'bold' : 'normal'}>
                                {'★'.repeat(n)} {p.overall_rating === n ? '✓' : ''}
                              </MenuItem>
                            ))}
                            {p.overall_rating && <MenuItem onClick={() => updateParticipant(p.id, 'overall_rating', null)}>Clear rating</MenuItem>}
                            <MenuDivider />
                            <MenuItem color="red.500" onClick={() => deleteParticipant(p.id)}>Delete participant</MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
                {participants.length > 0 && (
                  <Tfoot bg="gray.50">
                    <Tr fontWeight="bold">
                      {/* Identity + Management + Engagement — no totals */}
                      <Td><Text fontSize="sm">Totals</Text></Td>
                      <Td></Td>
                      <Td></Td><Td></Td><Td></Td><Td></Td>
                      <Td></Td>
                      {/* Raw count totals */}
                      <Td isNumeric>{participants.reduce((s, p) => s + parseInt(p.plans_created || 0), 0)}</Td>
                      <Td isNumeric>{participants.reduce((s, p) => s + parseInt(p.chat_count || 0), 0)}</Td>
                      <Td isNumeric>{participants.reduce((s, p) => s + parseInt(p.strategy_generations || 0), 0)}</Td>
                      <Td isNumeric>{participants.reduce((s, p) => s + parseInt(p.competitor_generations || 0), 0)}</Td>
                      <Td fontSize="xs">{participants.filter(p => p.has_action_plans).length}/{participants.length}</Td>
                      <Td isNumeric bg="brand.100">{participants.reduce((s, p) => s + parseInt(p.exports_made || 0), 0)}</Td>
                      <Td isNumeric bg="brand.100">{participants.reduce((s, p) => s + parseInt(p.shares_created || 0), 0)}</Td>
                      <Td fontSize="xs" bg="brand.100">{participants.filter(p => p.has_mcp).length}/{participants.length}</Td>
                      <Td isNumeric bg="brand.200">{participants.reduce((s, p) => s + parseInt(p.learnings_count || 0), 0)}</Td>
                      <Td isNumeric bg="brand.200">{participants.reduce((s, p) => s + parseInt(p.agent_task_count || 0), 0)}</Td>
                      <Td isNumeric bg="brand.200">{participants.reduce((s, p) => s + parseInt(p.pulses_count || 0), 0)}</Td>
                      <Td isNumeric>{participants.reduce((s, p) => s + (p.total_generations || 0), 0)}</Td>
                      <Td><Badge colorScheme="green" variant="subtle" fontSize="xs">{participants.filter(p => p.plan_type === 'pro').length} pro</Badge></Td>
                      <Td isNumeric>{participants.reduce((s, p) => s + (p.upgrade_intents || 0), 0)}</Td>
                      {/* Pilot timeline fractions */}
                      <Td fontSize="xs">{participants.filter(p => p.week1_active).length}/{participants.length}</Td>
                      <Td fontSize="xs">{participants.filter(p => p.week2_3_active_auto).length}/{participants.length}</Td>
                      <Td></Td>
                    </Tr>
                  </Tfoot>
                )}
              </Table>
            </Box>

          </TabPanel>

          <TabPanel px={0}>
            {feedbackSummary && (
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
                <Card variant="outline" size="sm">
                  <CardBody py={3}>
                    <Stat size="sm">
                      <StatLabel fontSize="xs">Total Notes</StatLabel>
                      <StatNumber>{feedbackSummary.total_notes}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card variant="outline" size="sm">
                  <CardBody py={3}>
                    <Stat size="sm">
                      <StatLabel fontSize="xs">With Feedback</StatLabel>
                      <StatNumber>{feedbackSummary.participants_with_feedback}/{feedbackSummary.total_participants}</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card variant="outline" size="sm">
                  <CardBody py={3}>
                    <Stat size="sm">
                      <StatLabel fontSize="xs">Engagement Rate</StatLabel>
                      <StatNumber>{feedbackSummary.total_participants > 0 ? Math.round((feedbackSummary.participants_with_feedback / feedbackSummary.total_participants) * 100) : 0}%</StatNumber>
                    </Stat>
                  </CardBody>
                </Card>
                <Card variant="outline" size="sm">
                  <CardBody py={3}>
                    <Stat size="sm">
                      <StatLabel fontSize="xs">By Type</StatLabel>
                      <Wrap spacing={1} mt={1}>
                        {(feedbackSummary.by_type || []).map(t => (
                          <WrapItem key={t.type}>
                            <Tag size="sm" colorScheme="blue" variant="subtle">
                              <TagLabel>{t.type.replace(/_/g, ' ')}: {t.count}</TagLabel>
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Stat>
                  </CardBody>
                </Card>
              </SimpleGrid>
            )}

            <Text fontSize="xs" color="gray.500" mb={3}>Click any count to view and manage notes for that participant and week. Structured feedback (scores) is editable inline.</Text>
            <Box overflowX="auto" bg="white" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th fontSize="xs" minW="160px" position="sticky" left={0} bg="gray.50" zIndex={1}>Participant</Th>
                    <Th fontSize="xs" minW="150px">Segment</Th>
                    <Th fontSize="xs" textAlign="center">Verdict</Th>
                    <Th fontSize="xs" textAlign="center">Wk1 Notes</Th>
                    <Th fontSize="xs" textAlign="center">Wk2-3 Notes</Th>
                    <Th fontSize="xs" textAlign="center">Wk4 Notes</Th>
                    <Th fontSize="xs" textAlign="center">Features</Th>
                    <Th fontSize="xs" textAlign="center">General</Th>
                    <Th fontSize="xs" textAlign="center">Total</Th>
                    <Th fontSize="xs" textAlign="center">Workflow Fit</Th>
                    <Th fontSize="xs" textAlign="center">Essential</Th>
                    <Th fontSize="xs" textAlign="center">Would Pay</Th>
                    <Th fontSize="xs" textAlign="center">Asked Pricing</Th>
                    <Th fontSize="xs" textAlign="center">Price</Th>
                    <Th fontSize="xs" textAlign="center">In Other Tools</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[...participants].sort((a, b) => {
                    const aNotes = (notesMap[a.id] || []).length;
                    const bNotes = (notesMap[b.id] || []).length;
                    if (bNotes !== aNotes) return bNotes - aNotes;
                    const vo = { 'Champion': 0, 'Engaged': 1, 'Exploring': 2, 'At Risk': 3, 'Inactive': 4, 'New': 5 };
                    return (vo[a.auto_verdict] ?? 6) - (vo[b.auto_verdict] ?? 6);
                  }).map((p) => {
                    const pNotes = notesMap[p.id] || [];
                    const wk1 = pNotes.filter(n => n.note_type === 'week1_feedback').length;
                    const wk23 = pNotes.filter(n => n.note_type === 'week2_3_feedback').length;
                    const wk4 = pNotes.filter(n => n.note_type === 'week4_feedback').length;
                    const features = pNotes.filter(n => n.note_type === 'feature_request').length;
                    const general = pNotes.filter(n => n.note_type === 'general').length;
                    const total = pNotes.length;

                    const openNotes = (type) => openPanel(p, type);

                    return (
                      <Tr key={p.id} _hover={{ bg: 'gray.50' }}>
                        <Td fontSize="xs" fontWeight="medium" position="sticky" left={0} bg="white" zIndex={1}>
                          <Text>{getName(p)}</Text>
                        </Td>
                        <Td><Badge variant="subtle" fontSize="xs">{p.segment}</Badge></Td>
                        <Td textAlign="center"><Badge colorScheme={verdictColor(p.auto_verdict)} variant="solid" fontSize="xs">{p.auto_verdict}</Badge></Td>
                        <Td textAlign="center">
                          <Badge cursor="pointer" colorScheme={wk1 > 0 ? 'blue' : 'gray'} variant={wk1 > 0 ? 'solid' : 'outline'} fontSize="xs" onClick={() => openNotes('week1_feedback')}>{wk1}</Badge>
                        </Td>
                        <Td textAlign="center">
                          <Badge cursor="pointer" colorScheme={wk23 > 0 ? 'teal' : 'gray'} variant={wk23 > 0 ? 'solid' : 'outline'} fontSize="xs" onClick={() => openNotes('week2_3_feedback')}>{wk23}</Badge>
                        </Td>
                        <Td textAlign="center">
                          <Badge cursor="pointer" colorScheme={wk4 > 0 ? 'orange' : 'gray'} variant={wk4 > 0 ? 'solid' : 'outline'} fontSize="xs" onClick={() => openNotes('week4_feedback')}>{wk4}</Badge>
                        </Td>
                        <Td textAlign="center">
                          <Badge cursor="pointer" colorScheme={features > 0 ? 'purple' : 'gray'} variant={features > 0 ? 'solid' : 'outline'} fontSize="xs" onClick={() => openNotes('feature_request')}>{features}</Badge>
                        </Td>
                        <Td textAlign="center">
                          <Badge cursor="pointer" colorScheme={general > 0 ? 'gray' : 'gray'} variant={general > 0 ? 'solid' : 'outline'} fontSize="xs" onClick={() => openNotes('general')}>{general}</Badge>
                        </Td>
                        <Td textAlign="center">
                          <Badge colorScheme={total > 0 ? 'green' : 'gray'} variant={total > 0 ? 'solid' : 'outline'} fontSize="xs">
                            {total}
                          </Badge>
                        </Td>
                        <Td textAlign="center">
                          <Select size="xs" w="60px" value={p.workflow_fit || ''} onChange={(e) => updateParticipant(p.id, 'workflow_fit', parseInt(e.target.value) || null)}>
                            <option value="">--</option>
                            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                          </Select>
                        </Td>
                        <Td textAlign="center">
                          <Select size="xs" w="60px" value={p.essential_score || ''} onChange={(e) => updateParticipant(p.id, 'essential_score', parseInt(e.target.value) || null)}>
                            <option value="">--</option>
                            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                          </Select>
                        </Td>
                        <Td textAlign="center">
                          <Badge
                            cursor="pointer"
                            colorScheme={p.would_pay ? 'green' : 'gray'}
                            variant={p.would_pay ? 'solid' : 'outline'}
                            onClick={() => updateParticipant(p.id, 'would_pay', !p.would_pay)}
                            fontSize="xs"
                          >
                            {p.would_pay ? 'Yes' : 'No'}
                          </Badge>
                        </Td>
                        <Td textAlign="center">
                          <Badge
                            cursor="pointer"
                            colorScheme={p.asked_about_pricing ? 'green' : 'gray'}
                            variant={p.asked_about_pricing ? 'solid' : 'outline'}
                            onClick={() => updateParticipant(p.id, 'asked_about_pricing', !p.asked_about_pricing)}
                            fontSize="xs"
                          >
                            {p.asked_about_pricing ? 'Yes' : 'No'}
                          </Badge>
                        </Td>
                        <Td textAlign="center">
                          <Input
                            size="xs"
                            w="70px"
                            value={p.price_point || ''}
                            placeholder="$"
                            onBlur={(e) => {
                              if (e.target.value !== (p.price_point || '')) updateParticipant(p.id, 'price_point', e.target.value);
                            }}
                            onChange={(e) => setParticipants(prev => prev.map(pp => pp.id === p.id ? { ...pp, price_point: e.target.value } : pp))}
                          />
                        </Td>
                        <Td textAlign="center">
                          <Badge
                            cursor="pointer"
                            colorScheme={p.references_in_other_tools ? 'green' : 'gray'}
                            variant={p.references_in_other_tools ? 'solid' : 'outline'}
                            onClick={() => updateParticipant(p.id, 'references_in_other_tools', !p.references_in_other_tools)}
                            fontSize="xs"
                          >
                            {p.references_in_other_tools ? 'Yes' : 'No'}
                          </Badge>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </TabPanel>

          <TabPanel px={0}>
            <Text fontSize="sm" color="gray.600" mb={4} lineHeight="1.6">
              Customer discovery view — identifies which user segment (Solo Founders, Product Leads, Consultants) shows the strongest commercial fit signals. This answers: <strong>who is PABS for?</strong>
            </Text>
            {dashboard ? (
              <>
                {dashboard.segment_performance && dashboard.segment_performance.length > 0 && (
                  <Box mt={6}>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>Segment Performance</Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>How each user segment is performing across key pilot metrics. Fit Score weights would-pay, essential rating, recent activity, and cohort-size confidence.</Text>
                    <Box bg="white" borderRadius="md" overflow="auto" boxShadow="sm">
                      <Table size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Segment Name</Th>
                            <Th textAlign="center"># Users</Th>
                            <Th textAlign="center">Fit Score</Th>
                            <Th textAlign="center">Active Rate %</Th>
                            <Th textAlign="center">Essential Rate %</Th>
                            <Th textAlign="center">Would Pay %</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {dashboard.segment_performance.map((sp, idx) => (
                            <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                              <Td><Text fontSize="sm" fontWeight="medium">{sp.segment}</Text></Td>
                              <Td textAlign="center"><Text fontSize="sm">{sp.user_count}</Text></Td>
                              <Td textAlign="center">
                                <Badge colorScheme={sp.fit_score >= 55 ? 'green' : sp.fit_score >= 35 ? 'yellow' : 'red'} variant="subtle" fontSize="xs">
                                  {sp.fit_score}%
                                </Badge>
                              </Td>
                              <Td textAlign="center">
                                <Badge colorScheme={sp.active_rate >= 70 ? 'green' : sp.active_rate >= 40 ? 'yellow' : 'red'} variant="subtle" fontSize="xs">
                                  {sp.active_rate}%
                                </Badge>
                              </Td>
                              <Td textAlign="center">
                                <Badge colorScheme={sp.essential_rate >= 60 ? 'green' : sp.essential_rate >= 30 ? 'yellow' : 'red'} variant="subtle" fontSize="xs">
                                  {sp.essential_rate}%
                                </Badge>
                              </Td>
                              <Td textAlign="center">
                                <Badge colorScheme={sp.would_pay_rate >= 50 ? 'green' : sp.would_pay_rate >= 25 ? 'yellow' : 'red'} variant="subtle" fontSize="xs">
                                  {sp.would_pay_rate}%
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {Object.keys(segmentStats).length > 0 && (
                  <Box mt={6}>
                    <Text fontSize="sm" fontWeight="bold" mb={3}>Segment Breakdown</Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      {Object.entries(segmentStats).map(([seg, stats]) => (
                        <Card key={seg} variant="outline" size="sm">
                          <CardBody>
                            <Text fontSize="sm" fontWeight="bold" mb={2}>{seg}</Text>
                            <HStack spacing={4} fontSize="xs" color="gray.600">
                              <Text>{stats.count} users</Text>
                              <Text>Avg: {stats.count > 0 ? Math.round(stats.totalScore / stats.count) : 0}</Text>
                              <Badge colorScheme="green" variant="subtle" fontSize="xs">{stats.champions} champ</Badge>
                              <Badge colorScheme="orange" variant="subtle" fontSize="xs">{stats.atRisk} at risk</Badge>
                            </HStack>
                          </CardBody>
                        </Card>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}

                {dashboard.conversion_readiness && dashboard.conversion_readiness.length > 0 && (
                  <Box mt={6}>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>Conversion Readiness</Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                      Combines Workflow Fit + Essential Score + Would Pay + Intent Fit to assess each participant's conversion likelihood. 
                      <strong> Ready</strong> = high fit + essential + willing to pay. 
                      <strong> Warm</strong> = strong signals in multiple areas. 
                      <strong> Potential</strong> = some positive signals. 
                      <strong> Exploring</strong> = has used PABS but no conversion signals yet. 
                      <strong> Cold</strong> = no meaningful signals.
                      Intent Fit shows how well their plan(s) align with PABS's core product strategy focus (strong/partial/weak).
                    </Text>
                    <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
                      {['ready', 'warm', 'potential', 'exploring', 'cold'].map(level => {
                        const count = (dashboard.conversion_readiness || []).filter(r => r.readiness === level).length;
                        const colors = { ready: 'green', warm: 'orange', potential: 'blue', exploring: 'yellow', cold: 'gray' };
                        return (
                          <Card key={level} variant="outline" borderColor={`${colors[level]}.200`} bg={`${colors[level]}.50`}>
                            <CardBody py={3} px={4} textAlign="center">
                              <Text fontSize="2xl" fontWeight="bold" color={`${colors[level]}.600`}>{count}</Text>
                              <Text fontSize="xs" color="gray.600" textTransform="capitalize">{level}</Text>
                            </CardBody>
                          </Card>
                        );
                      })}
                    </SimpleGrid>
                    <Box bg="white" borderRadius="md" overflow="auto" boxShadow="sm">
                      <Table size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Participant</Th>
                            <Th>Segment</Th>
                            <Th textAlign="center">Workflow Fit</Th>
                            <Th textAlign="center">Essential</Th>
                            <Th textAlign="center">Would Pay</Th>
                            <Th textAlign="center">Plans</Th>
                            <Th textAlign="center">Strategies</Th>
                            <Th textAlign="center">Days Inactive</Th>
                            <Th textAlign="center">Intent Fit</Th>
                            <Th textAlign="center">Readiness</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(dashboard.conversion_readiness || [])
                            .sort((a, b) => {
                              const order = { ready: 0, warm: 1, potential: 2, exploring: 3, cold: 4 };
                              return (order[a.readiness] ?? 5) - (order[b.readiness] ?? 5);
                            })
                            .map((r, idx) => {
                              const colors = { ready: 'green', warm: 'orange', potential: 'blue', exploring: 'yellow', cold: 'gray' };
                              return (
                                <Tr key={idx} _hover={{ bg: 'gray.50' }}>
                                  <Td><Text fontSize="sm" fontWeight="medium">{r.name}</Text></Td>
                                  <Td><Text fontSize="xs" color="gray.600">{r.segment}</Text></Td>
                                  <Td textAlign="center">
                                    <Text fontSize="sm" fontWeight={r.workflowFit >= 4 ? 'bold' : 'normal'} color={r.workflowFit >= 4 ? 'green.600' : r.workflowFit >= 3 ? 'yellow.600' : 'gray.400'}>
                                      {r.workflowFit || '—'}
                                    </Text>
                                  </Td>
                                  <Td textAlign="center">
                                    <Text fontSize="sm" fontWeight={r.essentialScore >= 4 ? 'bold' : 'normal'} color={r.essentialScore >= 4 ? 'green.600' : r.essentialScore >= 3 ? 'yellow.600' : 'gray.400'}>
                                      {r.essentialScore || '—'}
                                    </Text>
                                  </Td>
                                  <Td textAlign="center">
                                    <Badge colorScheme={r.wouldPay ? 'green' : 'gray'} variant="subtle" fontSize="xs">
                                      {r.wouldPay ? 'Yes' : 'No'}
                                    </Badge>
                                  </Td>
                                  <Td textAlign="center"><Text fontSize="sm">{r.plansCreated}</Text></Td>
                                  <Td textAlign="center"><Text fontSize="sm">{r.strategyCount}</Text></Td>
                                  <Td textAlign="center">
                                    <Text fontSize="sm" color={r.daysSinceActive <= 3 ? 'green.600' : r.daysSinceActive <= 7 ? 'yellow.600' : 'red.500'}>
                                      {r.daysSinceActive}d
                                    </Text>
                                  </Td>
                                  <Td textAlign="center">
                                    <Tooltip label={r.intentFitReason || ''} hasArrow placement="top" maxW="260px">
                                      <Badge
                                        colorScheme={r.intentFit === 'strong' ? 'green' : r.intentFit === 'partial' ? 'yellow' : r.intentFit === 'weak' ? 'red' : 'gray'}
                                        variant="subtle" fontSize="xs" textTransform="capitalize" cursor="help"
                                      >
                                        {r.intentFit || '—'}
                                      </Badge>
                                    </Tooltip>
                                  </Td>
                                  <Td textAlign="center">
                                    <Badge colorScheme={colors[r.readiness]} variant="solid" fontSize="xs" textTransform="capitalize">
                                      {r.readiness}
                                    </Badge>
                                  </Td>
                                </Tr>
                              );
                            })}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                )}

                {dashboard.feedback_themes && Object.keys(dashboard.feedback_themes).length > 0 && (
                  <Box mt={6}>
                    <Text fontSize="sm" fontWeight="bold" mb={1}>Feedback Themes</Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                      Key feedback patterns from pilot participants, grouped by feedback type. These insights help identify what's working, what needs improvement, and common user perceptions.
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {Object.entries(dashboard.feedback_themes).map(([type, data]) => (
                        <Card key={type} variant="outline">
                          <CardBody>
                            <Flex justify="space-between" align="center" mb={2}>
                              <Text fontSize="sm" fontWeight="medium" textTransform="capitalize">{type.replace(/_/g, ' ')} Feedback</Text>
                              <Badge colorScheme="blue" variant="subtle" fontSize="xs">{data.count} note{data.count !== 1 ? 's' : ''}</Badge>
                            </Flex>
                            <VStack align="stretch" spacing={2}>
                              {data.entries.map((entry, idx) => (
                                <Box key={idx} p={2} bg="gray.50" borderRadius="md">
                                  <Text fontSize="xs" color="gray.700" lineHeight="1.5" noOfLines={3}>"{entry.content}"</Text>
                                  <Text fontSize="xs" color="gray.400" mt={1}>— {entry.participant} ({entry.segment})</Text>
                                </Box>
                              ))}
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
              </>
            ) : (
              <Box textAlign="center" py={10}>
                <Text color="gray.400">No dashboard data</Text>
              </Box>
            )}
          </TabPanel>

          <TabPanel px={0}>
            <AdminPilotOutreachTab participants={participants} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Drawer isOpen={!!panelParticipant} placement="right" onClose={closePanel} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" pb={3}>
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="bold">{panelParticipant && getName(panelParticipant)}</Text>
              <HStack spacing={2}>
                {panelParticipant && <Badge colorScheme={verdictColor(panelParticipant.auto_verdict)} fontSize="xs">{panelParticipant.auto_verdict}</Badge>}
                {panelParticipant && <Badge variant="outline" fontSize="xs">{panelParticipant.segment}</Badge>}
                {panelParticipant && <Text fontSize="xs" color="gray.400">{(notesMap[panelParticipant.id] || []).length} total notes</Text>}
              </HStack>
            </VStack>
          </DrawerHeader>

          <DrawerBody px={4} py={4}>
            {!selectedNote && <Box bg="gray.50" p={4} borderRadius="md" mb={5} border="1px solid" borderColor="gray.200">
              <Text fontSize="xs" fontWeight="bold" mb={3} color="gray.700" textTransform="uppercase" letterSpacing="wide">Add New Feedback</Text>
              <Flex gap={3} mb={3}>
                <Box flex="1">
                  <Text fontSize="xs" color="gray.500" mb={1}>Category</Text>
                  <select
                    value={newNoteType}
                    onChange={(e) => setNewNoteType(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', fontSize: '13px', border: '1px solid #E2E8F0', borderRadius: '4px', backgroundColor: 'white', outline: 'none', height: '32px' }}
                  >
                    <option value="week1_feedback">Week 1 Feedback</option>
                    <option value="week2_3_feedback">Week 2-3 Feedback</option>
                    <option value="week4_feedback">Week 4 Feedback</option>
                    <option value="feature_request">Feature Request</option>
                    <option value="key_quotes">Key Quote</option>
                    <option value="general">General Note</option>
                  </select>
                </Box>
              </Flex>
              <Box mb={3}>
                <Text fontSize="xs" color="gray.500" mb={1}>Content</Text>
                <textarea
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  placeholder="Enter feedback, observation, or quote..."
                  rows={3}
                  onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey && newNoteContent.trim()) addNoteFromPanel(); }}
                  style={{ width: '100%', padding: '8px', fontSize: '13px', border: '1px solid #E2E8F0', borderRadius: '4px', backgroundColor: 'white', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5' }}
                />
                <Text fontSize="xs" color="gray.400" mt={1}>Cmd+Enter to submit</Text>
              </Box>
              <Flex align="center" justify="space-between" mb={3}>
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>Sentiment</Text>
                  <HStack spacing={1}>
                    {[1, 2, 3, 4, 5].map(s => (
                      <Box
                        key={s}
                        as="button"
                        onClick={() => setNewNoteRating(newNoteRating === s ? 0 : s)}
                        cursor="pointer"
                        color={s <= newNoteRating ? '#F6AD55' : '#E2E8F0'}
                        fontSize="18px"
                        lineHeight="1"
                        transition="color 0.1s"
                        _hover={{ color: s <= newNoteRating ? '#ED8936' : '#CBD5E0' }}
                      >
                        ★
                      </Box>
                    ))}
                    {newNoteRating > 0 && <Text fontSize="xs" color="gray.400" ml={1}>{newNoteRating}/5</Text>}
                  </HStack>
                </Box>
                <Text fontSize="xs" color="gray.400">{newNoteRating === 0 ? 'No rating' : newNoteRating <= 2 ? 'Negative' : newNoteRating === 3 ? 'Neutral' : 'Positive'}</Text>
              </Flex>
              <Button size="xs" colorScheme="blue" onClick={addNoteFromPanel} isDisabled={!newNoteContent.trim()} leftIcon={<AddIcon />} w="100%" h="32px" fontSize="xs">
                Add {newNoteType.replace(/_/g, ' ')}
              </Button>
            </Box>}

            {!selectedNote ? (
              <Box mb={3}>
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="sm" fontWeight="bold">Notes</Text>
                  <select
                    value={panelFilter}
                    onChange={(e) => setPanelFilter(e.target.value)}
                    style={{ padding: '4px 6px', fontSize: '12px', border: '1px solid #E2E8F0', borderRadius: '4px', backgroundColor: 'white' }}
                  >
                    <option value="all">All Categories</option>
                    <option value="week1_feedback">Week 1</option>
                    <option value="week2_3_feedback">Week 2-3</option>
                    <option value="week4_feedback">Week 4</option>
                    <option value="feature_request">Features</option>
                    <option value="key_quotes">Quotes</option>
                    <option value="general">General</option>
                  </select>
                </Flex>

                {panelParticipant && (() => {
                  const allNotes = notesMap[panelParticipant.id] || [];
                  const filtered = panelFilter === 'all' ? allNotes : allNotes.filter(n => n.note_type === panelFilter);
                  const typeColors = {
                    week1_feedback: 'blue',
                    week2_3_feedback: 'teal',
                    week4_feedback: 'orange',
                    feature_request: 'purple',
                    key_quotes: 'yellow',
                    general: 'gray',
                  };

                  if (filtered.length === 0) {
                    return (
                      <Box textAlign="center" py={8} bg="gray.50" borderRadius="md">
                        <Text fontSize="sm" color="gray.400">No {panelFilter === 'all' ? '' : panelFilter.replace(/_/g, ' ') + ' '}notes yet</Text>
                        <Text fontSize="xs" color="gray.300" mt={1}>Use the form above to add one</Text>
                      </Box>
                    );
                  }

                  return (
                    <VStack spacing={1} align="stretch">
                      {filtered.map(note => {
                        const cs = typeColors[note.note_type] || 'gray';
                        const title = note.content.length > 60 ? note.content.substring(0, 60) + '...' : note.content;
                        const stars = note.rating ? '★'.repeat(note.rating) + '☆'.repeat(5 - note.rating) : null;
                        return (
                          <Box
                            key={note.id}
                            p={2}
                            borderRadius="md"
                            border="1px solid"
                            borderColor="gray.100"
                            cursor="pointer"
                            _hover={{ bg: 'gray.50', borderColor: 'gray.200' }}
                            onClick={() => { setSelectedNote(note); setEditingNote(null); }}
                            transition="all 0.15s"
                          >
                            <Flex justify="space-between" align="center">
                              <HStack spacing={2} flex={1} minW={0}>
                                <Badge fontSize="9px" colorScheme={cs} variant="subtle" flexShrink={0}>{note.note_type.replace(/_/g, ' ')}</Badge>
                                <Text fontSize="xs" color="gray.700" noOfLines={1} flex={1}>{title}</Text>
                              </HStack>
                              <HStack spacing={2} flexShrink={0} ml={2}>
                                {stars && <Text fontSize="xs" color="orange.400" letterSpacing="1px">{stars}</Text>}
                                <Text fontSize="xs" color="gray.400">{formatDate(note.created_at)}</Text>
                                <ChevronRightIcon color="gray.300" boxSize={3} />
                              </HStack>
                            </Flex>
                          </Box>
                        );
                      })}
                    </VStack>
                  );
                })()}
              </Box>
            ) : (
              <Box>
                <Button size="xs" variant="ghost" mb={3} onClick={() => { setSelectedNote(null); setEditingNote(null); }} leftIcon={<ChevronRightIcon transform="rotate(180deg)" />} fontSize="xs" color="blue.500">
                  Back to list
                </Button>

                {(() => {
                  const typeColors = {
                    week1_feedback: 'blue', week2_3_feedback: 'teal', week4_feedback: 'orange',
                    feature_request: 'purple', key_quotes: 'yellow', general: 'gray',
                  };
                  const cs = typeColors[selectedNote.note_type] || 'gray';
                  const stars = selectedNote.rating || 0;

                  return (
                    <Box>
                      <Flex justify="space-between" align="center" mb={3}>
                        <Badge fontSize="xs" colorScheme={cs} variant="subtle">{selectedNote.note_type.replace(/_/g, ' ')}</Badge>
                        <Text fontSize="xs" color="gray.400">{formatDate(selectedNote.created_at)}</Text>
                      </Flex>

                      <Box mb={4}>
                        <Text fontSize="xs" color="gray.500" mb={1}>Sentiment Rating</Text>
                        <HStack spacing={1}>
                          {[1, 2, 3, 4, 5].map(s => (
                            <Box
                              key={s}
                              as="button"
                              onClick={() => updateNoteRating(selectedNote.id, panelParticipant.id, stars === s ? null : s)}
                              cursor="pointer"
                              color={s <= stars ? '#F6AD55' : '#E2E8F0'}
                              fontSize="20px"
                              lineHeight="1"
                              transition="color 0.1s"
                              _hover={{ color: s <= stars ? '#ED8936' : '#CBD5E0' }}
                            >
                              ★
                            </Box>
                          ))}
                          <Text fontSize="xs" color="gray.400" ml={2}>{stars === 0 ? 'Not rated' : stars <= 2 ? 'Negative' : stars === 3 ? 'Neutral' : 'Positive'}</Text>
                        </HStack>
                      </Box>

                      <Divider mb={3} />

                      {editingNote === selectedNote.id ? (
                        <VStack spacing={3} align="stretch">
                          <textarea
                            value={editNoteValue}
                            onChange={(e) => setEditNoteValue(e.target.value)}
                            rows={8}
                            style={{ width: '100%', padding: '10px', fontSize: '13px', border: '1px solid #E2E8F0', borderRadius: '4px', backgroundColor: 'white', outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.6' }}
                          />
                          <HStack>
                            <Button size="xs" colorScheme="green" onClick={async () => { await updateNote(selectedNote.id, panelParticipant.id); const updated = (notesMap[panelParticipant.id] || []).find(n => n.id === selectedNote.id); if (updated) setSelectedNote({ ...selectedNote, content: editNoteValue }); }}>Save</Button>
                            <Button size="xs" variant="outline" onClick={() => setEditingNote(null)}>Cancel</Button>
                          </HStack>
                        </VStack>
                      ) : (
                        <Box>
                          <Text fontSize="sm" lineHeight="1.7" whiteSpace="pre-wrap" color="gray.700">{selectedNote.content}</Text>
                        </Box>
                      )}

                      <Divider my={4} />

                      <HStack spacing={2}>
                        <Button size="xs" variant="outline" leftIcon={<EditIcon />} onClick={() => { setEditingNote(selectedNote.id); setEditNoteValue(selectedNote.content); }}>
                          Edit
                        </Button>
                        <Button size="xs" variant="outline" colorScheme="red" leftIcon={<DeleteIcon />} onClick={async () => { await deleteNote(selectedNote.id, panelParticipant.id); setSelectedNote(null); }}>
                          Delete
                        </Button>
                      </HStack>
                    </Box>
                  );
                })()}
              </Box>
            )}
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px">
            <Button size="sm" variant="outline" onClick={closePanel}>Close</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

    </Box>
  );
};

export default AdminPilotTracker;
